import { Button, FormControlLabel, Radio, RadioGroup, styled, Typography } from "@mui/material";
import { Suspense, useEffect, useState } from "react";
import { ErrorBoundary } from "react-error-boundary";
import { Pane } from "src/components/Pane";
import { useCaseShapeSelectionLayers } from "src/hooks";
import type { FeatureSelectionLayer } from "src/types";
import { parseWMSFeatureInfoParams } from "src/utils/ogc.utils";
import * as OGCService from "src/services/ogc.service";
import type { Feature } from "geojson";
import { useTranslation } from "react-i18next";

interface Props {
  selectedLayer: FeatureSelectionLayer | null;
  setSelectedLayer: (layer: FeatureSelectionLayer) => void;
  onShapeSelected: (feature: Feature) => void;
  onPrevious: () => void;
  onNext: () => void;
  feature: Feature | null;
  map: L.Map | null;
}

export function CasePickShape(props: Props) {
  return (
    <Styles>
      <Typography variant="h4" color="primary">
        Pick a case
      </Typography>
      <ErrorBoundary fallback={null}>
        <Suspense fallback>
          <LoadData {...props} />
        </Suspense>
      </ErrorBoundary>
    </Styles>
  );
}

function LoadData(props: Props) {
  const { selectedLayer, setSelectedLayer, onShapeSelected, map, feature, onNext, onPrevious } = props;
  const { t } = useTranslation();
  const { data: layers } = useCaseShapeSelectionLayers();
  const layer = selectedLayer || layers?.at(0);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!map) return;

    async function handleClick(event: L.LeafletMouseEvent) {
      setIsLoading(true);
      const clickWMSParams = parseWMSFeatureInfoParams(event);
      const featureInfo = await OGCService.getWMSFeatureInfo({
        ...clickWMSParams,
        layers: layer?.geoserverLayer || "",
        query_layers: layer?.geoserverLayer || "",
      });
      setIsLoading(false);
      const feature = featureInfo.features[0];
      if (!feature) return;
      onShapeSelected(feature);
    }
    map.on("click", handleClick);

    return () => {
      map.off("click", handleClick);
    };
  }, [map, layer, onShapeSelected]);

  return (
    <>
      <RadioGroup
        value={layer?.id}
        onChange={(e) => {
          const layer = layers?.find((layer) => layer.id === Number.parseInt(e.target.value));
          if (!layer) return;
          setSelectedLayer(layer);
        }}
      >
        {(layers || []).map((layer) => (
          <FormControlLabel key={layer.id} value={layer.id} control={<Radio />} label={layer.nameTranslation} />
        ))}
      </RadioGroup>
      {!feature && !isLoading && <div>Select a case on the map</div>}
      <div className="list-container">
        {!isLoading &&
          feature &&
          layer &&
          layer.propertyList.map((p) => {
            console.log(p);
            return (
              <div key={p.id} className="list-item">
                <Typography variant="body1">{p.nameTranslation}:</Typography>
                <Typography variant="body2">{feature.properties?.[p.nameKey.toUpperCase()]}</Typography>
              </div>
            );
          })}
      </div>
      {isLoading && <div>Loading...</div>}
      <div className="button-container">
        <Button variant="text" onClick={onPrevious}>
          {t("label.previous")}
        </Button>
        <Button variant="contained" onClick={onNext}>
          {t("label.next")}
        </Button>
      </div>
    </>
  );
}

const Styles = styled(Pane)`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(3)};

  .list-container {
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing(1)};
  }

  .button-container {
    width: 100%;
    display: flex;
    flex-direction: row;
    gap: ${({ theme }) => theme.spacing(1)};
    justify-content: flex-end;
  }
`;
